// Types.
import type { direction } from '../Types/Direction';
import type event from '../Types/Event';
import type source from '../Types/Source';
import type { directionCallback as callback } from '../Types/Callbacks';

// Constants.
import { directions } from '../Types/Direction';

// Interfaces.
import { DirectionOptions } from '../Interfaces/Options';

// Classes.
import Handler from './Handler';
import Listener from './Listener';

/**
 * Scrolly.Direction
 *
 * @author: FelixKiesewetter <info@felixkiesewetter.de>
 * @lastModified: 2026-06-10 Fixed TS errors.
 * @package fk_js/scrolly
 * @version: 1.2.0
 */
class Direction extends Handler<DirectionOptions>
{

    private lastScrollTop ?:number;
    private lastScrollDirection ?:direction;

    /**
     * Constructor.
     */
    constructor(options :DirectionOptions, source :source)
    {
        super(options);

        // Change data attributes on <body>.
        if (this.options.dataset) {
            window.addEventListener('scrollUp', () => {
                setTimeout(() :void => {
                    document.body.dataset.scrollyDirection = directions.UP.toString();
                }, this.options.datasetDelay);
            });
            window.addEventListener('scrollDown', () => {
                setTimeout(() :void => {
                    document.body.dataset.scrollyDirection = directions.DOWN.toString();
                }, this.options.datasetDelay);
            });
            window.addEventListener('scrollTop', () => {
                document.body.removeAttribute('data-scrolly-direction');
            });
        }
    }

    /**
     * Define default options.
     */
    public defaults() :DirectionOptions
    {
        return {
            dataset: false,
            datasetDelay: 25,
            deadzone: 0,
            debug: false,
            onChange: null
        };
    }

    /**
     * This callback listens to the scroll event.
     */
    public events() :event[]
    {
        return [ 'scroll' ];
    }

    /**
     * Check if an animation frame should be requested or not.
     */
    public requestAnimationFrames(event :Event, handler :Listener) :Function[]
    {
        let   animationFrames  :Function[] = [];
        const currentScrollTop :number = window.pageYOffset || document.documentElement.scrollTop;
        const scrollUpEvent    :CustomEvent = new CustomEvent('scrollUp');
        const scrollDownEvent  :CustomEvent = new CustomEvent('scrollDown');
        const scrollTopEvent   :CustomEvent = new CustomEvent('scrollTop');

        // Scroll position has not changed at all.
        if (currentScrollTop === this.lastScrollTop) {
            return animationFrames;
        }

        // Scroll position has changed.
        else {

            // Update last scroll position.
            animationFrames.push(() => {
                this.lastScrollTop = Math.max(0, currentScrollTop);
                if (Math.max(0, currentScrollTop) === 0 || (this.options.deadzone && Math.max(currentScrollTop) < this.options.deadzone)) {
                    window.dispatchEvent(scrollTopEvent);
                }
            });

            // Check if the scroll direction has changed.
            const currentScrollDirection :direction = (this.lastScrollTop || 0) > currentScrollTop ? directions.UP : directions.DOWN;
            if (currentScrollDirection !== this.lastScrollDirection) {
                animationFrames.push(() :void => {
                    this.options.onChange && this.options.onChange({ direction: currentScrollDirection });
                    this.lastScrollDirection = currentScrollDirection;
                    if (directions.DOWN === currentScrollDirection) {
                        window.dispatchEvent(scrollDownEvent);
                    }
                    else {
                        window.dispatchEvent(scrollUpEvent);
                    }
                });
            }
        }

        return animationFrames;
    }

    // #######
    // Getters
    // #######

    /**
     * Getter for the current scroll direction.
     */
    public get() :direction|undefined
    {
        return this.lastScrollDirection;
    }

    /**
     * Getter for the current scroll direction.
     */
    public getDirection() :direction|undefined
    {
        return this.get();
    }

    /**
     * Getter for the current scroll direction.
     */
    public getScrollDirection() :direction|undefined
    {
        return this.get();
    }

    // #######
    // Setters
    // #######

    /**
     * Setter for the onChange callback.
     */
    public onChange(onChange :callback) :Direction
    {
        this.options.onChange = onChange;
        return this;
    }

    // ##############
    // Static methods
    // ##############

    /**
     * Observe the scroll direction of an element or window.
     */
    static observe(source :source = window, options :DirectionOptions) :Direction
    {
        return new Direction(options, source);
    }

}

export default Direction;